/**
 * Calendar Sync Settings Component
 *
 * Renders calendar sync entries (Google Calendar, Apple/Outlook) in the
 * right panel of the Guide Availability Page. Each entry opens an inline
 * drawer with subscribe URL and setup instructions.
 *
 * MVP 1.12.4 C-06 / C-07
 * Stories: SCRUM-1165, SCRUM-1166
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import apiRequest from '../../services/api';

/**
 * CalendarSyncSettings
 *
 * @component
 */
export default function CalendarSyncSettings() {
  const [tokenStatus, setTokenStatus] = useState({
    hasActiveToken: false,
    subscribeUrl: null,
    loaded: false,
  });
  const [openDrawer, setOpenDrawer] = useState(null); // 'google' | 'apple' | null
  const [loading, setLoading] = useState(false);
  const [tokenError, setTokenError] = useState(null);
  const [copyFeedback, setCopyFeedback] = useState(false);
  const drawerRef = useRef(null);

  // Fetch token status on mount
  useEffect(() => {
    let cancelled = false;

    async function fetchStatus() {
      try {
        const result = await apiRequest('/api/v1/calendar/feed/token/status');
        if (!cancelled) {
          setTokenStatus({
            hasActiveToken: result.data.hasActiveToken,
            subscribeUrl: result.data.subscribeUrl,
            loaded: true,
          });
        }
      } catch {
        if (!cancelled) {
          setTokenStatus(prev => ({ ...prev, loaded: true }));
        }
      }
    }

    fetchStatus();
    return () => { cancelled = true; };
  }, []);

  // Close drawer on outside click
  useEffect(() => {
    if (!openDrawer) { return; }

    function handleClickOutside(e) {
      if (drawerRef.current && !drawerRef.current.contains(e.target)) {
        setOpenDrawer(null);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openDrawer]);

  /**
   * Open a drawer. If no active token, generate one first.
   */
  const handleEntryClick = useCallback(async (drawerId) => {
    if (loading) { return; }

    if (!tokenStatus.hasActiveToken) {
      setLoading(true);
      setTokenError(null);
      try {
        const result = await apiRequest('/api/v1/calendar/feed/token', {
          method: 'POST',
        });
        setTokenStatus({
          hasActiveToken: true,
          subscribeUrl: result.data.subscribeUrl,
          loaded: true,
        });
      } catch {
        setTokenError('Couldn\u2019t set up calendar sync. Please try again.');
        setLoading(false);
        setOpenDrawer(drawerId);
        return;
      }
      setLoading(false);
    }

    setOpenDrawer(drawerId);
  }, [loading, tokenStatus.hasActiveToken]);

  /**
   * Copy subscribe URL to clipboard
   */
  const handleCopy = useCallback(async () => {
    if (!tokenStatus.subscribeUrl) { return; }
    try {
      await navigator.clipboard.writeText(tokenStatus.subscribeUrl);
      setCopyFeedback(true);
      setTimeout(() => setCopyFeedback(false), 2000);
    } catch {
      // Fallback: no-op if clipboard unavailable
    }
  }, [tokenStatus.subscribeUrl]);

  const badgeClasses = tokenStatus.hasActiveToken
    ? 'text-white text-xs font-medium px-2 py-0.5 rounded-full'
    : 'bg-gray-100 text-gray-500 text-xs font-medium px-2 py-0.5 rounded-full';

  const badgeStyle = tokenStatus.hasActiveToken
    ? { backgroundColor: '#009263' }
    : {};

  const badgeLabel = tokenStatus.hasActiveToken ? 'Active' : 'Off';

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 mt-4">
      <h3 className="text-sm font-semibold text-gray-900 mb-3">Calendar sync</h3>

      {/* Google Calendar entry */}
      <button
        type="button"
        onClick={() => handleEntryClick('google')}
        disabled={loading}
        className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg hover:bg-gray-50 transition-colors text-left"
        data-testid="google-calendar-entry"
      >
        <span className="text-sm text-gray-800">Google Calendar</span>
        <span className={badgeClasses} style={badgeStyle} data-testid="google-calendar-badge">
          {badgeLabel}
        </span>
      </button>

      {/* Google Calendar drawer */}
      {openDrawer === 'google' && (
        <div ref={drawerRef} className="mt-2 border border-gray-200 rounded-lg p-4 bg-gray-50" data-testid="google-calendar-drawer">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold text-gray-900">Google Calendar</h4>
            <button
              type="button"
              onClick={() => setOpenDrawer(null)}
              className="text-gray-400 hover:text-gray-600 text-lg leading-none"
              aria-label="Close drawer"
              data-testid="close-drawer"
            >
              &times;
            </button>
          </div>

          {/* Token generation error */}
          {tokenError && (
            <p role="alert" className="text-sm text-red-600 mb-3" data-testid="token-error">
              {tokenError}
            </p>
          )}

          {/* Subscribe URL */}
          {!tokenError && (
            <>
              <div className="flex gap-2 mb-4">
                <input
                  type="text"
                  readOnly
                  value={tokenStatus.subscribeUrl || ''}
                  className="flex-1 text-xs px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-700 truncate"
                  data-testid="subscribe-url"
                />
                <button
                  type="button"
                  onClick={handleCopy}
                  className="px-3 py-2 text-xs font-medium text-white rounded-lg whitespace-nowrap"
                  style={{ backgroundColor: '#009263' }}
                  data-testid="copy-button"
                >
                  {copyFeedback ? 'Copied!' : 'Copy'}
                </button>
              </div>

              {/* Setup instructions */}
              <div className="text-xs text-gray-600 space-y-1.5 mb-4">
                <p className="font-medium text-gray-800">Setup instructions:</p>
                <ol className="list-decimal list-inside space-y-1">
                  <li>Open Google Calendar &rarr; Settings &rarr; Other calendars &rarr; From URL</li>
                  <li>Paste the link</li>
                  <li>Click Add calendar</li>
                </ol>
              </div>
            </>
          )}

          {/* Footer link */}
          <a
            href="/guide/settings?tab=integrations"
            className="text-xs font-medium hover:underline"
            style={{ color: '#009263' }}
            data-testid="more-settings-link"
          >
            More calendar settings &rarr;
          </a>
        </div>
      )}

      {/* Apple, Outlook & others entry */}
      <button
        type="button"
        onClick={() => handleEntryClick('apple')}
        disabled={loading}
        className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg hover:bg-gray-50 transition-colors text-left"
        data-testid="apple-calendar-entry"
      >
        <span className="text-sm text-gray-800">Apple, Outlook &amp; others</span>
        <span className={badgeClasses} style={badgeStyle} data-testid="apple-calendar-badge">
          {badgeLabel}
        </span>
      </button>

      {/* Apple, Outlook & others drawer */}
      {openDrawer === 'apple' && (
        <div ref={drawerRef} className="mt-2 border border-gray-200 rounded-lg p-4 bg-gray-50" data-testid="apple-calendar-drawer">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold text-gray-900">Apple, Outlook &amp; others</h4>
            <button
              type="button"
              onClick={() => setOpenDrawer(null)}
              className="text-gray-400 hover:text-gray-600 text-lg leading-none"
              aria-label="Close drawer"
              data-testid="close-apple-drawer"
            >
              &times;
            </button>
          </div>

          {/* Token generation error */}
          {tokenError && (
            <p role="alert" className="text-sm text-red-600 mb-3" data-testid="apple-token-error">
              {tokenError}
            </p>
          )}

          {/* Subscribe URL — same as Google Calendar */}
          {!tokenError && (
            <>
              <div className="flex gap-2 mb-4">
                <input
                  type="text"
                  readOnly
                  value={tokenStatus.subscribeUrl || ''}
                  className="flex-1 text-xs px-3 py-2 border border-gray-300 rounded-lg bg-white text-gray-700 truncate"
                  data-testid="apple-subscribe-url"
                />
                <button
                  type="button"
                  onClick={handleCopy}
                  className="px-3 py-2 text-xs font-medium text-white rounded-lg whitespace-nowrap"
                  style={{ backgroundColor: '#009263' }}
                  data-testid="apple-copy-button"
                >
                  {copyFeedback ? 'Copied!' : 'Copy'}
                </button>
              </div>

              {/* Apple Calendar setup instructions */}
              <div className="text-xs text-gray-600 space-y-1.5 mb-3">
                <p className="font-medium text-gray-800">Setup instructions:</p>
                <ol className="list-decimal list-inside space-y-1">
                  <li>Open Apple Calendar &rarr; File &rarr; New Calendar Subscription</li>
                  <li>Paste the link and click Subscribe</li>
                  <li>Choose refresh frequency (Every hour recommended)</li>
                </ol>
              </div>

              {/* Provider note */}
              <p className="text-xs text-gray-500 mb-4" data-testid="provider-note">
                Also works with Outlook, Fantastical, and any app that supports iCal subscriptions. For Outlook setup instructions, see More calendar settings.
              </p>
            </>
          )}

          {/* Footer link */}
          <a
            href="/guide/settings?tab=integrations"
            className="text-xs font-medium hover:underline"
            style={{ color: '#009263' }}
            data-testid="apple-more-settings-link"
          >
            More calendar settings &rarr;
          </a>
        </div>
      )}
    </div>
  );
}
